import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Alert, Card } from 'react-bootstrap';
import { categoriesApi } from '../services/api';

const CategoryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    ativo: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (isEditing) {
      fetchCategory();
    }
  }, [id, navigate, isEditing]);

  const fetchCategory = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await categoriesApi.getById(parseInt(id));
      setFormData({
        nome: data.nome,
        ativo: data.ativo,
      });
    } catch (err: any) {
      console.error('Error loading category:', err);
      setError('Erro ao carregar categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.nome?.trim()) {
      setError('O nome é obrigatório');
      return;
    }

    setLoading(true);

    try {
      if (isEditing) {
        await categoriesApi.update(parseInt(id!), formData);
        setSuccess('Categoria atualizada com sucesso!');
      } else {
        await categoriesApi.create(formData);
        setSuccess('Categoria criada com sucesso!');
      }

      setTimeout(() => {
        navigate('/admin/categorias');
      }, 1500);
    } catch (err: any) {
      console.error('Error saving category:', err);
      setError(err.message || 'Erro ao salvar categoria');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-tags me-2"></i>
          {isEditing ? 'Editar Categoria' : 'Nova Categoria'}
        </h2>
      </div>

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Nome *</Form.Label>
              <Form.Control
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
                placeholder="Digite o nome da categoria"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                label="Categoria ativa"
                checked={formData.ativo}
                onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Categorias inativas não aparecem na lista de seleção ao criar notícias.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => navigate('/admin/categorias')}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-2"></i>
                    {isEditing ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CategoryForm;
